import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { issuesApi } from '../api/issues'
import { useAuthStore } from '../store/authStore'
import { PriorityBadge } from '../components/common/PriorityBadge'
import type { Issue } from '../types'

const statusColors: Record<string, string> = {
  TODO: '#94a3b8',
  IN_PROGRESS: '#6366f1',
  DONE: '#22c55e',
}

const priorityRank: Record<Issue['priority'], number> = {
  URGENT: 0,
  HIGH: 1,
  MEDIUM: 2,
  LOW: 3,
  NO_PRIORITY: 4,
}

function StatusDot({ category }: { category: string }) {
  return (
    <span
      style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: statusColors[category] || '#94a3b8',
        display: 'inline-block',
        flexShrink: 0,
      }}
    />
  )
}

function dueRank(issue: Issue) {
  return issue.dueDate ? new Date(issue.dueDate).getTime() : Number.MAX_SAFE_INTEGER
}

function compareIssues(a: Issue, b: Issue) {
  const priorityDiff = priorityRank[a.priority] - priorityRank[b.priority]
  if (priorityDiff !== 0) return priorityDiff
  const dueDiff = dueRank(a) - dueRank(b)
  if (dueDiff !== 0) return dueDiff
  return a.identifier.localeCompare(b.identifier)
}

function IssueRow({ issue, onClick }: { issue: Issue; onClick: () => void }) {
  const [hovered, setHovered] = useState(false)

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 130px 90px',
        alignItems: 'center',
        gap: 12,
        padding: '9px 14px',
        background: hovered ? '#f8fafc' : '#ffffff',
        cursor: 'pointer',
        borderBottom: '1px solid #f1f5f9',
        transition: 'background 0.1s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <StatusDot category={issue.status?.category || 'TODO'} />
        <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#94a3b8', flexShrink: 0 }}>
          {issue.identifier}
        </span>
        <span
          style={{
            fontSize: 13.5,
            color: '#1e293b',
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {issue.title}
        </span>
        {issue.status?.name && (
          <span
            style={{
              fontSize: 11,
              background: '#f1f5f9',
              color: '#64748b',
              padding: '1px 6px',
              borderRadius: 4,
              flexShrink: 0,
            }}
          >
            {issue.status.name}
          </span>
        )}
      </div>

      <PriorityBadge priority={issue.priority} size="sm" />

      <div style={{ fontSize: 12, color: '#64748b' }}>
        {issue.dueDate ? format(new Date(issue.dueDate), 'MMM d') : '-'}
      </div>
    </div>
  )
}

export default function MyIssuesPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [filter, setFilter] = useState<'all' | 'in_progress' | 'todo'>('all')

  const { data, isLoading } = useQuery({
    queryKey: ['my-issues'],
    queryFn: () => issuesApi.listMyIssues(),
    enabled: !!user,
  })

  const allIssues: Issue[] = data?.data?.data || []

  const filtered = allIssues.filter((issue) => {
    if (filter === 'in_progress') return issue.status?.category === 'IN_PROGRESS'
    if (filter === 'todo') return issue.status?.category === 'TODO'
    return true
  })

  const groupedProjects = useMemo(() => {
    const groups = filtered.reduce<Record<string, { name: string; issues: Issue[] }>>((acc, issue) => {
      const projectId = issue.project?.id || 'unknown'
      if (!acc[projectId]) {
        acc[projectId] = {
          name: issue.project?.name || 'No Project',
          issues: [],
        }
      }
      acc[projectId].issues.push(issue)
      return acc
    }, {})

    return Object.entries(groups)
      .map(([projectId, group]) => ({
        projectId,
        name: group.name,
        issues: [...group.issues].sort(compareIssues),
      }))
      .sort((a, b) => {
        const issueDiff = compareIssues(a.issues[0], b.issues[0])
        return issueDiff !== 0 ? issueDiff : a.name.localeCompare(b.name)
      })
  }, [filtered])

  const filterBtn = (label: string, value: typeof filter) => (
    <button
      onClick={() => setFilter(value)}
      style={{
        padding: '5px 14px',
        fontSize: 13,
        fontWeight: 500,
        border: 'none',
        borderRadius: 6,
        cursor: 'pointer',
        background: filter === value ? '#6366f1' : '#f1f5f9',
        color: filter === value ? '#fff' : '#475569',
        transition: 'all 0.15s',
      }}
    >
      {label}
    </button>
  )

  return (
    <div style={{ padding: 24, width: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', margin: 0 }}>My Issues</h1>
          <p style={{ fontSize: 13, color: '#64748b', margin: '4px 0 0' }}>
            {allIssues.length} issue{allIssues.length !== 1 ? 's' : ''} assigned to you
          </p>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {filterBtn('All', 'all')}
          {filterBtn('In Progress', 'in_progress')}
          {filterBtn('To Do', 'todo')}
        </div>
      </div>

      <div style={{ background: '#ffffff', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 130px 90px',
            gap: 12,
            padding: '10px 14px',
            background: '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
            fontSize: 11,
            fontWeight: 700,
            color: '#94a3b8',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          <span>Issue</span>
          <span>Priority</span>
          <span>Due</span>
        </div>

        {isLoading ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>Loading...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
            No issues assigned to you
          </div>
        ) : (
          groupedProjects.map((group) => (
            <div key={group.projectId}>
              <div
                style={{
                  padding: '10px 14px',
                  fontSize: 13,
                  fontWeight: 800,
                  color: '#1e293b',
                  background: '#f8fafc',
                  borderBottom: '1px solid #f1f5f9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                }}
              >
                <span>{group.name}</span>
                <span style={{ color: '#64748b', fontSize: 12, fontWeight: 700 }}>
                  {group.issues.length} issue{group.issues.length === 1 ? '' : 's'}
                </span>
              </div>
              {group.issues.map((issue) => (
                <IssueRow key={issue.id} issue={issue} onClick={() => navigate(`/issues/${issue.id}`)} />
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  )
}
